import { Action, Store } from '@ngrx/store';
import { isDefined } from 'ionic-angular/util/util';
import { fromPromise } from 'rxjs/observable/fromPromise';
import { IStorage } from '../../services/storage/storage.interface';
import { ucFirst } from '../../util/uc-first';
import { PersistableAction } from '../actions/base-actions';
import { StateHelper } from '../helpers/statehelper';

interface ActionCollection {
    entity: {[key: string]: Array<PersistableAction>};
    filters: {[key: string]: Array<PersistableAction>};
    selected: {[key: string]: Array<PersistableAction>};
    value: {[key: string]: Array<PersistableAction>};
}

export class StateHydrator {

    constructor(public storage: IStorage, protected store: Store<any>) {

    }

    async getState(): Promise<any> {

        return fromPromise(this.getActions())
            .map(actions => {

                // initialise am object that we will use to collect actions that
                // were persisted, we group them by action type
                const initCollection: ActionCollection = {
                    entity: {},
                    filters: {},
                    selected: {},
                    value: {}
                };

                return actions
                    // filter out any actions that we don't know how to
                    // rehydrate
                    .filter(a =>
                        isDefined(a.action.type) &&
                        isDefined(a.action.entity) &&
                        isDefined(a.action.options)
                    )
                    // reduce the array of actions to the collection we
                    // initialised above
                    .reduce((p, c) => {
                        const key = c.key;
                        const action = c.action;
                        const parsed = StateHelper.parseStorageKey(key);

                        if (!p[parsed.keyType]) {
                            console.error(
                                `unknown action type ${parsed.keyType}`
                            );
                            return p;
                        }
                        if (!p[parsed.keyType][parsed.entity]) {
                            p[parsed.keyType][parsed.entity] = [];
                        }
                        p[parsed.keyType][parsed.entity].push(action);
                        return p;
                    }, initCollection);
            })
            .do(collection => {
                // Rehydrate the entities
                Object
                    .keys(collection.entity)
                    .forEach(key => {
                        this.handleEntityActions(
                            collection.entity[key],
                            key
                        );
                    });
                // Rehydrate single values e.g. tokens etc
                Object
                    .keys(collection.value)
                    .forEach(key => {
                        collection.value[key]
                            .forEach(action => {
                                this.handleAction(action);
                            });
                    });

                // Rehydrate the selected entity e.g. account, user etc
                // This needs to come after we have rehydrated the entities or
                // some selectors will fail.
                Object
                    .keys(collection.selected)
                    .sort((a, b) => {

                        if (a === 'user') {
                            return -1;
                        }
                        return a.localeCompare(b);
                    })
                    .forEach(key => {
                        collection.selected[key]
                            .forEach(action => {
                                this.handleAction(action);
                            });
                    });

                // Rehydrate the filters.
                // This needs to come after we have rehydrated the entities or
                // some selectors will fail.
                Object
                    .keys(collection.filters)
                    .forEach(key => {
                        collection.filters[key]
                            .forEach(action => {
                                this.handleAction(action);
                            });
                    });

            })
            .toPromise();
    }

    protected async getActions(): Promise<Array<{key: string, action: PersistableAction}>> {
        const actions: Array<{key: string, action: PersistableAction}> = [];
        await this.storage.forEach((action: PersistableAction, key: string) => {
            actions.push({
                action,
                key
            });
        });
        return actions;
    }

    protected handleAction(action: PersistableAction): void {
        this.store.dispatch({
            ...action,
            options: {
                ...action.options,
                restful: false,
                persistable: true
            }
        } as PersistableAction);
    }

    protected handleEntityActions(
        actions: Array<PersistableAction>,
        entity: string
    ): void {

        const type = `[${ucFirst(entity)}] Rehydrate`;
        const payload = actions.map(action => action.payload);

        this.store.dispatch({
            entity,
            type,
            payload,
            options: {
                restful: false,
                persistable: false
            }
        } as Action);
    }
}



// WEBPACK FOOTER //
// ./src/core/store/hydrator/state-hydrator.ts